import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Opening Statement Section */}
      <section className="pt-56 pb-28 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-semibold text-[#1A2332] mb-8 leading-tight">
            We practice law the way business should work: clearly, directly, and without unnecessary complexity
          </h1>
          <p className="text-xl text-[#6B7280] leading-relaxed">
            Semeiks Law was built on the belief that legal help should be practical, understandable, and focused on real solutions rather than billable hours.
          </p>
        </div>
      </section>

      {/* Philosophy Cards Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-5 bg-[#2C3540] rounded-3xl p-12 md:p-16">
              <p className="text-sm font-semibold text-[#C4704B] uppercase tracking-wider mb-6">
                What we believe
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 leading-tight">
                Practical over performative
              </h2>
              <p className="text-base text-white/90 leading-relaxed">
                Good legal work doesn't need to be complicated. We focus on giving you clear advice and strong documents that actually protect you, not impressive-sounding language that nobody understands. Our approach is straightforward: understand your situation, explain your options, and help you make informed decisions.
              </p>
            </div>
            <div className="md:col-span-7 bg-[#F8F9FA] rounded-3xl p-12 md:p-16 md:mt-16">
              <p className="text-sm font-semibold text-[#C4704B] uppercase tracking-wider mb-6">
                How we work
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1A2332] mb-6 leading-tight">
                Clear communication, smart solutions
              </h2>
              <p className="text-base text-[#374151] leading-relaxed">
                We take time to understand what you're trying to accomplish, then provide legal guidance that fits your actual needs. No cookie-cutter templates, no unnecessary steps, and no confusing legal jargon. You'll always know what we're doing, why we're doing it, and what it will cost.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Experience Timeline */}
      <section className="py-28 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-[#1A2332] mb-16 text-center">
            Built on real experience
          </h2>
          <div className="relative pl-12">
            <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-[#C4704B]/20"></div>
            
            <div className="relative mb-16">
              <div className="absolute left-[-3.1rem] top-2 w-4 h-4 rounded-full bg-[#C4704B] border-4 border-white"></div>
              <div className="ml-8">
                <h3 className="text-xl font-bold text-[#1A2332] mb-3">Before Law Practice</h3>
                <h4 className="text-lg font-semibold text-[#C4704B] mb-3">Corporate &amp; Operational Experience</h4>
                <p className="text-base text-[#6B7280] leading-relaxed">
                  Before becoming an attorney, I spent years working in corporate and operational roles. That experience provided firsthand exposure to how businesses operate, grow, manage risk, and make real-world decisions under pressure.
                </p>
                <p className="text-base text-[#6B7280] leading-relaxed mt-3">
                  This background continues to shape how I approach legal work, with a focus on practicality, efficiency, and business realities rather than abstract legal theory.
                </p>
              </div>
            </div>

            <div className="relative mb-16">
              <div className="absolute left-[-3.1rem] top-2 w-4 h-4 rounded-full bg-[#C4704B] border-4 border-white"></div>
              <div className="ml-8">
                <h3 className="text-xl font-bold text-[#1A2332] mb-3">Legal Training &amp; Transition</h3>
                <h4 className="text-lg font-semibold text-[#C4704B] mb-3">Bringing a Business Mindset to Law</h4>
                <p className="text-base text-[#6B7280] leading-relaxed">
                  After transitioning into the legal field, I brought that business-first perspective into my legal practice. Rather than treating legal issues in isolation, I focus on how contracts, structure, and legal decisions impact day-to-day operations and long-term goals.
                </p>
                <p className="text-base text-[#6B7280] leading-relaxed mt-3">
                  This approach allows clients to receive legal guidance that aligns with how their business actually functions.
                </p>
              </div>
            </div>

            <div className="relative">
              <div className="absolute left-[-3.1rem] top-2 w-4 h-4 rounded-full bg-[#C4704B] border-4 border-white"></div>
              <div className="ml-8">
                <h3 className="text-xl font-bold text-[#1A2332] mb-3">Today</h3>
                <h4 className="text-lg font-semibold text-[#C4704B] mb-3">Practical Legal Guidance for Real Businesses</h4>
                <p className="text-base text-[#6B7280] leading-relaxed">
                  Today, Semeiks Law works with individuals and business owners who want clear, straightforward legal help. The focus remains the same: practical solutions, strong legal foundations, and guidance that supports informed decision-making without unnecessary complexity.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Grid */}
      <section className="py-28 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-16">
            <div>
              <h3 className="text-3xl font-bold text-[#1A2332] mb-4">Clarity</h3>
              <p className="text-base text-[#6B7280] leading-relaxed">
                Legal work should be understandable. We explain things in plain language and make sure you know exactly what's happening at every step.
              </p>
            </div>
            <div>
              <h3 className="text-3xl font-bold text-[#1A2332] mb-4">Practicality</h3>
              <p className="text-base text-[#6B7280] leading-relaxed">
                We focus on solutions that work in the real world. No theoretical exercises or unnecessary complications, just practical legal help that serves your actual needs.
              </p>
            </div>
            <div>
              <h3 className="text-3xl font-bold text-[#1A2332] mb-4">Integrity</h3>
              <p className="text-base text-[#6B7280] leading-relaxed">
                We're honest about what you need and what you don't. If something isn't necessary, we'll tell you. Our goal is to help you, not to maximize billable hours.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Closing CTA Section */}
      <section className="py-24 px-6 bg-[#F5F6F7]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#1A2332] mb-6">
            Let's discuss your situation
          </h2>
          <p className="text-lg text-[#6B7280] mb-10 leading-relaxed">
            Every engagement starts with a conversation about what you need and how we can help.
          </p>
          <Link 
            to="/contact"
            className="inline-block bg-[#C4704B] text-white px-10 py-4 rounded-lg text-base font-medium hover:bg-[#A85A3A] transition-colors duration-300 whitespace-nowrap"
          >
            Schedule a consultation
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
